import { Skeleton } from '@mantine/core'

import AnalyticsCard from './AnalyticsCard'

export interface StatRowItem {
  /** Stable key, e.g. an enum value or slug. */
  id: string
  /** Human-readable label (e.g. "Hired"). */
  label: string
  count: number
  /** Accent bar color. Pastel mid-tones (Tailwind 300-shade) read best. */
  color: string
  /**
   * When provided, the row becomes interactive (hover + cursor + keyboard
   * accessible) and fires this handler. Omit to render a static stat row.
   */
  onClick?: () => void
}

export interface StatRowsCardViewProps {
  title: string
  info?: string
  items: ReadonlyArray<StatRowItem>
  loading?: boolean
  /** Suffix shown after the percentage (e.g. "of period", "of total"). */
  pctSuffix?: string
  /** Subtext shown on rows when the total is 0. */
  emptySubtext?: string
}

const formatPct = (pct: number): string => {
  if (pct === 0) return '0%'
  if (pct >= 10) return `${pct.toFixed(0)}%`
  return `${pct.toFixed(1)}%`
}

interface StatRowProps {
  label: string
  count: number
  total: number
  color: string
  pctSuffix: string
  emptySubtext: string
  onClick?: () => void
}

const StatRow = ({
  label,
  count,
  total,
  color,
  pctSuffix,
  emptySubtext,
  onClick,
}: StatRowProps) => {
  const subText =
    total === 0
      ? emptySubtext
      : `${formatPct((count / total) * 100)} ${pctSuffix}`

  const clickable = typeof onClick === 'function'

  const className = [
    'flex items-center gap-3 rounded-lg bg-gray-50 px-4 py-3',
    clickable
      ? 'cursor-pointer transition-colors hover:bg-doubleNickel-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-doubleNickel-brand-300'
      : '',
  ]
    .filter(Boolean)
    .join(' ')

  const handleKeyDown = clickable
    ? (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.()
        }
      }
    : undefined

  return (
    <div
      className={className}
      onClick={clickable ? onClick : undefined}
      onKeyDown={handleKeyDown}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      data-testid={clickable ? 'stat-row-clickable' : undefined}
    >
      <div
        className="h-10 w-1 flex-shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      <div className="flex flex-1 items-center justify-between">
        <div>
          <span className="text-xs font-medium text-gray-600">{label}</span>
          <p className="text-[11px] text-gray-400">{subText}</p>
        </div>
        <div className="flex items-baseline">
          <span className="text-2xl font-medium tabular-nums text-gray-800">
            {count.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  )
}

/**
 * A category in a stat-rows summary — the static palette half (id + label +
 * accent color). Pair with a counts lookup via `buildCategoryStatRowItems`
 * to produce the data half (`StatRowItem[]`).
 */
export interface CategoryDefinition {
  id: string
  label: string
  /** Pastel mid-tone (Tailwind 300-shade) reads best as an accent bar. */
  color: string
}

/**
 * Maps a palette of categories + a per-id counts lookup into the
 * `StatRowItem[]` shape expected by `StatRowsCardView`. Used by any
 * "summary card with one row per category" surface (e.g. Pipeline stages,
 * call statuses, document statuses). Missing keys in `counts` resolve to 0
 * so callers don't have to materialize zeros explicitly.
 *
 * The optional `onCategoryClick` factory receives the category id and
 * returns the row's handler (or `undefined` for non-interactive rows).
 */
export const buildCategoryStatRowItems = (
  categories: ReadonlyArray<CategoryDefinition>,
  counts: Readonly<Record<string, number>>,
  onCategoryClick?: (id: string) => (() => void) | undefined
): ReadonlyArray<StatRowItem> =>
  categories.map((c) => ({
    id: c.id,
    label: c.label,
    color: c.color,
    count: counts[c.id] ?? 0,
    onClick: onCategoryClick?.(c.id),
  }))

export const StatRowsCardView = ({
  title,
  info,
  items,
  loading = false,
  pctSuffix = 'of total',
  emptySubtext = 'No items',
}: StatRowsCardViewProps) => {
  const total = items.reduce((sum, item) => sum + item.count, 0)
  const skeletonCount = Math.max(items.length, 3)

  // Important: do NOT pass `loading` to AnalyticsCard. AnalyticsCard's
  // `loading` prop replaces children with a tiny spinner, which would make
  // the skeleton rows below unreachable. Owning the loading branch here
  // (mirrors TrendChartView) lets the skeletons render properly during
  // refetches when `notifyOnNetworkStatusChange: true`.
  return (
    <AnalyticsCard title={title} info={info}>
      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <Skeleton key={i} height={64} radius="lg" />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((item) => (
            <StatRow
              key={item.id}
              label={item.label}
              count={item.count}
              total={total}
              color={item.color}
              pctSuffix={pctSuffix}
              emptySubtext={emptySubtext}
              onClick={item.onClick}
            />
          ))}
        </div>
      )}
    </AnalyticsCard>
  )
}
